import {
	ReactElement,
	useEffect,
	useState
} from "react";
import { GridCellPixelValuesTracker, useGridCellPixelValuesState } from "../GridCellPixelValuesTracker";
import { TileGrid } from "../TileGrid";
import { EmbedTileGroupViewModel } from "client/Api/Workspace/EmbedTileGroup/embedTileGroup";
import {
	defaultTileGridConfiguration,
	groupWidthToTileGroupColumnSpan,
	TileGridRenderState,
	TileGroupColumnSpanInTileGroupGrid,
	TileGroupInitializationModel,
	WorkspaceInitializationModel
} from "../RenderState/common";
import { convertToInitialization } from "../RenderState/conversion";
import { applyRenderSettings, generateInitialState } from "../RenderState/reducers/tileGrid";
import { InteractionState } from "../Tiles/TileContext";
import { TileGroupWidth } from "client/Api/Workspace/workspace";
import {
	EffectiveTheme,
	useEffectiveTheme,
	useUserTimezone
} from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { TileContentDependencies, useTileContentDependencies } from "../useTileContentDependencies";
import { TileCallbacks } from "../Tile";
import { TileGridChangeEffect, TileGridReducerContext } from "../RenderState/reducers/effects";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	hasFlag
} from "client/Misc/utils";
import { createRoot } from "react-dom/client";
import classNames from "classnames";
import { LiveTileHeaderStyle } from "client/Api/Branding/branding";
import { createTileGridRenderSettings } from "../RenderState/selectors/tileGroup";
import { Theme } from "client/Api/Branding/UserTheme/userTheme";
import { TileGridConfigurationTracker, useTileGridConfigurationState } from "../TileGridConfigurationTracker";
import styles from "./EmbedTileGroup.module.less";
import { getTileGridPixelStyles } from "../utils";

export type EmbedTileGroupProps = {
	model: EmbedTileGroupViewModel;
};

export const EmbedTileGroup = (props: EmbedTileGroupProps): ReactElement => {
	const {
		model
	} = props;

	const [renderState, setRenderState] = useState<TileGridRenderState>(
		(): TileGridRenderState => getInitialState(model)
	);

	const [tileGridConfiguration, setTileGridConfiguration] = useTileGridConfigurationState();
	const [tileGridCellPixelValues, setTileGridCellPixelValues] = useGridCellPixelValuesState();

	const userTimezone: TimeZone = useUserTimezone();

	const effectiveTheme: EffectiveTheme = useEffectiveTheme();

	const tileContentDependencies: TileContentDependencies = useTileContentDependencies(
		model.tileFactoryConfiguration
	);

	useEffect(
		(): void => {
			setRenderState(
				renderState => {
					const context = new TileGridReducerContext();

					return applyEffect(
						applyRenderSettings(
							renderState,
							context,
							createTileGridRenderSettings(
								model.automaticallyReorderTiles,
								tileGridConfiguration,
								getColumnSpanForGroup(
									model.tileGroup
										.width
								)
							)
						),
						context
					);
				}
			);
		},
		[
			model.automaticallyReorderTiles,
			model.tileGroup
				.width,
			tileGridConfiguration
		]
	);

	return (
		<div
			className={
				classNames(
					styles.root,
					{
						"colored-live-tile-header-style": model.liveTileHeaderStyle === LiveTileHeaderStyle.coloredBackground
							&& effectiveTheme === Theme.Light
					}
				)
			}
			style={getTileGridPixelStyles(tileGridCellPixelValues)}
		>
			<TileGrid
				renderState={renderState}
				cellPixelValues={tileGridCellPixelValues}
				displacementSinceTileDragStarted={undefined}
				isHidden={false}
				isTileGroupEditable={false}
				isInEditMode={false}
				userTimezone={userTimezone}
				tileCallbacks={nonEditableTileCallbacks}
				tileContentDependencies={tileContentDependencies}
			/>
			<GridCellPixelValuesTracker
				kind={
					model.isPercentileTileScalingEnabledForEmbedTileGroup
						? "tile-percentile"
						: "tile"
				}
				onChange={setTileGridCellPixelValues}
			/>
			<TileGridConfigurationTracker
				onNewConfiguration={setTileGridConfiguration}
			/>
		</div>
	);
};
EmbedTileGroup.displayName = "EmbedTileGroup";

const editIsNotAllowed = (): never => {
	throw new Error("Editing tiles is not allowed ");
};

const nonEditableTileCallbacks: TileCallbacks = {
	onDelete: editIsNotAllowed,
	onDragMove: editIsNotAllowed,
	onDragStart: editIsNotAllowed,
	onDragStop: editIsNotAllowed,
	onEdit: editIsNotAllowed,
	onResize: editIsNotAllowed
};

function getInitialState(
	model: EmbedTileGroupViewModel
): TileGridRenderState {
	const initializationModel: WorkspaceInitializationModel = convertToInitialization(
		[model.tileGroup],
		model.maintenanceWindows ?? EMPTY_ARRAY,
		model.conditionalAccessDisabledApps ?? EMPTY_ARRAY,
		EMPTY_ARRAY
	);

	if (initializationModel.groups.length !== 1) {
		throw new Error("Unexpected number of groups.");
	}

	const group: TileGroupInitializationModel = initializationModel.groups[0];

	const context = new TileGridReducerContext();

	const renderState: TileGridRenderState = generateInitialState(
		context,
		group.tiles,
		InteractionState.Default,
		createTileGridRenderSettings(
			model.automaticallyReorderTiles,
			defaultTileGridConfiguration,
			getColumnSpanForGroup(
				group.data
					.width
			)
		)
	);

	return applyEffect(
		renderState,
		context
	);
}

function getColumnSpanForGroup(
	width: TileGroupWidth
): TileGroupColumnSpanInTileGroupGrid {
	const tileGroupGridColumnSpan: number = groupWidthToTileGroupColumnSpan[width];

	return {
		value: tileGroupGridColumnSpan,
		kind: "normal"
	};
}

function applyEffect(
	tileGridRenderState: TileGridRenderState,
	context: TileGridReducerContext
): TileGridRenderState {
	if (hasFlag(context.resultEffect, TileGridChangeEffect.HeightIsChanged)) {
		// Nothing to do
	}

	return tileGridRenderState;
}

export function initialize(model: EmbedTileGroupViewModel): void {
	if (!model.accessiblityState.isAccessible) {
		return;
	}

	const host: HTMLElement = ensureNotNil(
		document.getElementById("embed-tile-group-host")
	);

	createRoot(host)
		.render(
			<EmbedTileGroup model={model} />
		);
}
